/**
 * Comparing two versions of a document for History: which blocks were added, removed or
 * changed, and within a changed block, which words. Works on ProseMirror JSON.
 */

export type BlockKind = "paragraph" | "h1" | "h2" | "h3" | "quote" | "bullet" | "ordered" | "break";

export interface Block {
  kind: BlockKind;
  text: string;
  /** Number of an ordered list item. */
  n?: number;
}

export type Op = "same" | "added" | "removed";

export interface Span {
  op: Op;
  text: string;
}

export type DiffBlock =
  | { op: Op; block: Block }
  | { op: "changed"; block: Block; spans: Span[] };

export interface DocDiff {
  blocks: DiffBlock[];
  wordsAdded: number;
  wordsRemoved: number;
}

interface JSONNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: JSONNode[];
  text?: string;
}

const inlineText = (node: JSONNode): string =>
  (node.content ?? []).map((c) => (c.type === "text" ? (c.text ?? "") : c.type === "hardBreak" ? "\n" : inlineText(c))).join("");

/** The document as a flat list of text blocks, in reading order. */
export function docBlocks(doc: unknown): Block[] {
  const out: Block[] = [];
  const walk = (nodes: JSONNode[] | undefined, inside: BlockKind | null) => {
    for (const node of nodes ?? []) {
      switch (node.type) {
        case "paragraph":
          out.push({ kind: inside ?? "paragraph", text: inlineText(node) });
          break;
        case "heading": {
          const level = Number(node.attrs?.level ?? 1);
          out.push({ kind: level === 1 ? "h1" : level === 2 ? "h2" : "h3", text: inlineText(node) });
          break;
        }
        case "horizontalRule":
          out.push({ kind: "break", text: "" });
          break;
        case "blockquote":
          walk(node.content, "quote");
          break;
        case "bulletList":
          walk(node.content, "bullet");
          break;
        case "orderedList": {
          let n = Number(node.attrs?.start ?? 1);
          for (const item of node.content ?? []) {
            const before = out.length;
            walk(item.content, "ordered");
            if (out.length > before) out[before]!.n = n++;
          }
          break;
        }
        default:
          walk(node.content, inside);
      }
    }
  };
  walk((doc as JSONNode | null)?.content, null);
  return out;
}

/** Above this many cells, the middle of a diff is shown as replaced rather than aligned. */
const MAX_CELLS = 4_000_000;

/**
 * Longest-common-subsequence diff of two sequences, after trimming the common start and end
 * (edits are usually local, so the part that needs the quadratic table is small).
 */
export function diffSequences<T>(a: T[], b: T[], key: (x: T) => string = String): { op: Op; item: T }[] {
  const ka = a.map(key);
  const kb = b.map(key);
  let start = 0;
  while (start < a.length && start < b.length && ka[start] === kb[start]) start++;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && ka[endA - 1] === kb[endB - 1]) {
    endA--;
    endB--;
  }

  const head = a.slice(0, start).map((item) => ({ op: "same" as const, item }));
  const tail = a.slice(endA).map((item) => ({ op: "same" as const, item }));
  const n = endA - start;
  const m = endB - start;
  const middle: { op: Op; item: T }[] = [];

  if (n * m > MAX_CELLS) {
    for (let i = start; i < endA; i++) middle.push({ op: "removed", item: a[i]! });
    for (let j = start; j < endB; j++) middle.push({ op: "added", item: b[j]! });
  } else {
    // lcs[i][j]: common length of a[start+i..endA) and b[start+j..endB).
    const w = m + 1;
    const lcs = new Uint32Array((n + 1) * w);
    for (let i = n - 1; i >= 0; i--) {
      for (let j = m - 1; j >= 0; j--) {
        lcs[i * w + j] =
          ka[start + i] === kb[start + j]
            ? lcs[(i + 1) * w + j + 1]! + 1
            : Math.max(lcs[(i + 1) * w + j]!, lcs[i * w + j + 1]!);
      }
    }
    let i = 0;
    let j = 0;
    while (i < n || j < m) {
      if (i < n && j < m && ka[start + i] === kb[start + j]) {
        middle.push({ op: "same", item: a[start + i]! });
        i++;
        j++;
      } else if (i < n && (j === m || lcs[(i + 1) * w + j]! >= lcs[i * w + j + 1]!)) {
        // On a tie, removals first: "-old +new" reads better than "+new -old".
        middle.push({ op: "removed", item: a[start + i]! });
        i++;
      } else {
        middle.push({ op: "added", item: b[start + j]! });
        j++;
      }
    }
  }
  return [...head, ...middle, ...tail];
}

/** Words with their trailing space, or a single punctuation mark with its trailing space. */
const TOKEN = /[\p{L}\p{M}\p{N}’']+\s*|[^\s\p{L}\p{M}\p{N}’']\s*|\s+/gu;
const tokens = (text: string) => text.match(TOKEN) ?? [];
const WORD = /[\p{L}\p{N}]/u;
const countWords = (text: string) => tokens(text).filter((t) => WORD.test(t)).length;

/** Word-level changes within one block. Removals come before additions in each changed run. */
export function diffText(before: string, after: string): Span[] {
  const spans: Span[] = [];
  let removed = "";
  let added = "";
  const flush = () => {
    if (removed) spans.push({ op: "removed", text: removed });
    if (added) spans.push({ op: "added", text: added });
    removed = added = "";
  };
  for (const { op, item } of diffSequences(tokens(before), tokens(after))) {
    if (op === "removed") removed += item;
    else if (op === "added") added += item;
    else {
      flush();
      const last = spans[spans.length - 1];
      if (last?.op === "same") last.text += item;
      else spans.push({ op: "same", text: item });
    }
  }
  flush();
  return spans;
}

const wordSet = (text: string) => new Set(text.toLowerCase().match(/[\p{L}\p{N}’']+/gu) ?? []);

/** Treat a removed and an added block as one edited block if they share enough words. */
function similar(a: Block, b: Block): boolean {
  if (a.kind === "break" || b.kind === "break") return false;
  const wa = wordSet(a.text);
  const wb = wordSet(b.text);
  if (!wa.size || !wb.size) return false;
  let shared = 0;
  for (const w of wa) if (wb.has(w)) shared++;
  return shared / Math.max(wa.size, wb.size) >= 0.4;
}

const blockKey = (b: Block) => `${b.kind}\u0000${b.text}`;

/** What changed from `before` to `after` (both ProseMirror JSON). */
export function diffDocs(before: unknown, after: unknown): DocDiff {
  const ops = diffSequences(docBlocks(before), docBlocks(after), blockKey);
  const blocks: DiffBlock[] = [];
  let wordsAdded = 0;
  let wordsRemoved = 0;

  // Each run of removed/added blocks between unchanged ones: pair up edited blocks.
  for (let i = 0; i < ops.length; ) {
    if (ops[i]!.op === "same") {
      blocks.push({ op: "same", block: ops[i]!.item });
      i++;
      continue;
    }
    const removed: Block[] = [];
    const added: Block[] = [];
    for (; i < ops.length && ops[i]!.op !== "same"; i++) (ops[i]!.op === "removed" ? removed : added).push(ops[i]!.item);

    let r = 0;
    let a = 0;
    while (r < removed.length || a < added.length) {
      const rb = removed[r];
      const ab = added[a];
      if (rb && ab && similar(rb, ab)) {
        const spans = diffText(rb.text, ab.text);
        for (const s of spans) {
          if (s.op === "added") wordsAdded += countWords(s.text);
          if (s.op === "removed") wordsRemoved += countWords(s.text);
        }
        blocks.push({ op: "changed", block: ab, spans });
        r++;
        a++;
      } else if (rb && (!ab || !added.slice(a).some((x) => similar(rb, x)))) {
        wordsRemoved += countWords(rb.text);
        blocks.push({ op: "removed", block: rb });
        r++;
      } else {
        wordsAdded += countWords(ab!.text);
        blocks.push({ op: "added", block: ab! });
        a++;
      }
    }
  }
  return { blocks, wordsAdded, wordsRemoved };
}
